import { type Locale } from 'date-fns';
import { enGB, nl, nlBE } from 'date-fns/locale';
import { type LngLatLike } from 'mapbox-gl';

type PayoutMethods = 'iban' | 'bban';
export interface IRegion {
  id: string;
  slug: string;
  flag: string;
  translationKey: string;
  postalCodeValidation: RegExp;
  supportEmailAddress: string;
  consentUrl: string;
  apiUrl: string;
  dashboardUrl: string;
  enabled: boolean | ((flags: [key: string]) => boolean);
  mapBoxURL: string;
  mapBoxTileSet: string;
  mapBoxGIDLevel: string;
  locale: string;
  dateFnsLocale: Locale;
  localeLocation: string;
  localeLngLat: LngLatLike;
  currency: string;
  payoutMethods: PayoutMethods[];
}

const regions: IRegion[] = [
  {
    id: 'nl',
    slug: 'nl-NL',
    flag: '/images/countries/NL_flag.svg',
    translationKey: 'namespaces:regions:nl:title',
    postalCodeValidation: /^[1-9][0-9]{3}[\s]?[A-Za-z]{2}$/,
    supportEmailAddress: 'support@youngones.works',
    consentUrl: 'https://youngones.com/nl/algemene-voorwaarden/',
    apiUrl: process.env.NEXT_PUBLIC_ADMIN_API_NL_URL ?? '',
    dashboardUrl: process.env.NEXT_PUBLIC_DASHBOARD_URL ?? '',
    enabled: true,
    mapBoxURL: 'mapbox://teamdevelopment.7srzg45o',
    mapBoxTileSet: 'gadm41_NLD_2_2-0nnnz5',
    mapBoxGIDLevel: '2',
    locale: 'nl-NL',
    dateFnsLocale: nl,
    localeLocation: 'Europe/Amsterdam',
    localeLngLat: [4.89707, 52.377956],
    currency: 'EUR',
    payoutMethods: ['iban'],
  },
  {
    id: 'gb',
    slug: 'en-GB',
    flag: '/images/countries/UK_flag.svg',
    translationKey: 'namespaces:regions:gb:title',
    postalCodeValidation: /^([A-Z][A-HJ-Y]?\d[A-Z\d]? ?\d[A-Z]{2}|GIR ?0A{2})$/,
    supportEmailAddress: 'support.uk@youngones.com',
    consentUrl: 'https://youngones.com/uk/terms-and-conditions/',
    apiUrl: process.env.NEXT_PUBLIC_ADMIN_API_GB_URL ?? '',
    dashboardUrl: process.env.NEXT_PUBLIC_DASHBOARD_URL ?? '',
    enabled: true,
    mapBoxURL: 'mapbox://teamdevelopment.5foh9n9w',
    mapBoxTileSet: 'gadm41_GBR_3-5f1fqq',
    mapBoxGIDLevel: '3',
    locale: 'en-US',
    dateFnsLocale: enGB,
    localeLocation: 'Europe/London',
    localeLngLat: [-0.1278, 51.5074],
    currency: 'GBP',
    payoutMethods: ['iban', 'bban'],
  },
  {
    id: 'be',
    slug: 'nl-BE',
    flag: '/images/countries/BE_flag.svg',
    translationKey: 'namespaces:regions:be:title',
    postalCodeValidation: /^[1-9][0-9]{3}$/,
    supportEmailAddress: 'support.be@youngones.com',
    consentUrl: 'https://youngones.com/be/algemene-voorwaarden/',
    apiUrl: process.env.NEXT_PUBLIC_ADMIN_API_BE_URL ?? '',
    dashboardUrl: process.env.NEXT_PUBLIC_DASHBOARD_URL ?? '',
    enabled: true,
    mapBoxURL: 'mapbox://teamdevelopment.1kixgq1s',
    mapBoxTileSet: 'gadm41_BEL_4-5kvjjf',
    mapBoxGIDLevel: '4',
    locale: 'nl-BE',
    dateFnsLocale: nlBE,
    localeLocation: 'Europe/Brussels',
    localeLngLat: [4.3517, 50.8503],
    currency: 'EUR',
    payoutMethods: ['iban'],
  },
  {
    id: 'fr',
    slug: 'fr-FR',
    flag: '/images/countries/FR_flag.svg',
    translationKey: 'namespaces:regions:fr:title',
    postalCodeValidation: /^(2[AB]|[0-9]{2})[0-9]{3}$/,
    supportEmailAddress: 'support.fr@youngones.com',
    consentUrl: 'https://youngones.com/fr/algemene-voorwaarden/',
    apiUrl: process.env.NEXT_PUBLIC_ADMIN_API_FR_URL ?? '',
    dashboardUrl: process.env.NEXT_PUBLIC_DASHBOARD_URL ?? '',
    enabled: true,
    mapBoxURL: 'mapbox://teamdevelopment.dmvk4ftq',
    mapBoxTileSet: 'gadm41_FRA_4-5iug4o',
    mapBoxGIDLevel: '4',
    locale: 'fr-FR',
    dateFnsLocale: enGB,
    localeLocation: 'Europe/Paris',
    localeLngLat: [2.3522, 48.8566],
    currency: 'EUR',
    payoutMethods: ['iban'],
  },
];

export const getRegions = (flags?: [key: string]) =>
  regions.filter((region) =>
    typeof region.enabled === 'boolean' ? region.enabled : flags !== undefined ? region.enabled(flags) : false,
  );

export const getRegionIds = () => regions.map((region) => region.id);

export const getRegion = (fromRegionPage = false) => {
  if (typeof window !== 'undefined') {
    const regionPathSegment = window.location.pathname.split('/')[1];

    const region = regions.find((region) => regionPathSegment === region.id);

    return region ? region : fromRegionPage ? null : regions[0];
  }

  return null;
};

export const getRegionBySlug = (slug: string) => {
  if (!slug) return null;

  return getRegions().find((region) => region.slug === slug);
};

export const getRegionByID = (id: string) => {
  if (!id) return null;

  return getRegions().find((region) => region.id === id);
};
